/**
 * The data of the stage, in the order it is useful (ui/DESIGN.md, "Data flow"): the preview first (≈ 20 ms,
 * enough for the kicker, the title and the lane skeletons), then the records (kinds and the list of one
 * kind), the runs and the overview in parallel, and only on request the list of another kind, the history
 * (changes lens) and more rows. Every call carries the trail, the range and the as-of moment through
 * scopeParams(), as pages/Data.tsx does. Earlier data stays while a reload is in flight (useAsync).
 */
import { useMemo, useRef } from 'react';
import { Api, type History, type Kind, type VaultError, type Overview, type Preview, type RecordKind, type RecordList, type Records, type Run, type Runs } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { scopeParams, type Step } from './scope.ts';

const SINGLE_TYPES = new Set(['Order', 'Invoice', 'Shipment']);
const leaf = (p: string) => (p.split('/').pop() ?? p).toLowerCase();

/** The data models whose key is the searched identifier: the thing's own kind(s). */
export function ownKinds(kinds: RecordKind[], preview: Preview | null | undefined, overview: Overview | null | undefined): RecordKind[] {
  const ids = (preview?.identities ?? overview?.identifiedBy ?? []).filter((i) => i.searched);
  const fields = new Set(ids.flatMap((i) => i.fields.map(leaf)));
  if (fields.size === 0) return [];
  return kinds.filter((k) => k.key.some((p) => fields.has(leaf(p))));
}

/**
 * Before the overview is in, the map still needs a Kind per data model: made from the record kinds and the
 * preview's classes (counts, systems, when the newest came); formats and failures follow with the overview.
 */
export function kindsOf(overview: Overview | null | undefined, records: Records | null | undefined, preview: Preview | null | undefined): Kind[] {
  if (overview?.kinds.length) return overview.kinds;
  const byCls = new Map((preview?.classes ?? []).map((c) => [c.cls, c]));
  return (records?.kinds ?? []).map((k): Kind => {
    const p = byCls.get(k.cls);
    return {
      cls: k.cls, system: k.system, total: p?.total ?? k.messages, sampled: 0, direct: true, via: [], status: [], format: null,
      histogram: [], first: null, last: p?.last ?? null, fields: [],
    };
  });
}

export interface StageData {
  scopeKey: string;
  preview: Preview | null | undefined;
  /** Looking as of a moment: the preview of the same window up to now, for the track and the ghosts of what came later. */
  full: Preview | null | undefined;
  /** Data models with documents in the window up to now but none as of the moment looked at: drawn as ghosts. */
  ghosts: string[];
  records: Records | null | undefined;
  /** The kinds of records found (the records' kinds, the overview's as a fallback). */
  kinds: RecordKind[];
  /** The Kind per data model the map draws from. */
  mapKinds: Kind[];
  /** The list the map and the records lens show: the kind asked for, else the cloud's default. */
  list: RecordList | null;
  /** The list of the thing's own kind, for a single record's sentence (an order's sales orders, not its lines). */
  ownList: RecordList | null;
  /** The searched thing is one record: its own kind and key. */
  single: { cls: string; key: string } | null;
  runs: Runs | null | undefined;
  /** Why the newest failed run of each failing integration failed (its failed step's info), by process; the three worst. */
  reasons: Record<string, string>;
  overview: Overview | null | undefined;
  history: History | null | undefined;
  /** Something is still on its way (the first answer is shown meanwhile). */
  loading: boolean;
  listLoading: boolean;
  /** What went wrong, quietly: errors of calls and nodes that did not answer, once each. */
  problems: string[];
  reload: () => void;
}

export function useStageData({ tid, q, steps, time, nowTime, kind, limit, wantHistory }: {
  tid: string; q: string; steps: Step[]; time: URLSearchParams;
  /** The same time params without the as-of moment, when there is one: what the window holds up to now. */
  nowTime: URLSearchParams | null;
  /** The data model whose records are listed (the records lens's kind, the map's selection). */
  kind: string | null;
  /** Rows to list (300 by default, 2000 for "show all"). */
  limit: number;
  wantHistory: boolean;
}): StageData {
  const scope = useMemo(() => scopeParams(q, steps, time), [q, steps, time]);
  const scopeKey = scope.toString();
  const live = !!q.trim();
  const withParams = (extra: Record<string, string>) => {
    const p = new URLSearchParams(scope);
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return p;
  };

  const preview = useAsync(() => (live ? Api.preview(tid, scope) : Promise.resolve(null)), [tid, scopeKey]);
  // The kinds and the cloud's default list (the data model most of the traffic is about) in one call
  const base = useAsync(() => (live ? Api.records(tid, withParams({ limit: String(limit) })) : Promise.resolve(null)), [tid, scopeKey, limit]);
  const runs = useAsync(() => (live ? Api.runs(tid, withParams({ max: '500' })) : Promise.resolve(null)), [tid, scopeKey]);
  const overview = useAsync(() => (live ? Api.overview(tid, scope) : Promise.resolve(null)), [tid, scopeKey]);
  const history = useAsync(() => (live && wantHistory ? Api.history(tid, scope) : Promise.resolve(null)), [tid, scopeKey, wantHistory]);
  const nowScope = useMemo(() => (nowTime ? scopeParams(q, steps, nowTime) : null), [q, steps, nowTime]);
  const nowKey = nowScope?.toString() ?? '';
  const full = useAsync(() => (live && nowScope ? Api.preview(tid, nowScope) : Promise.resolve(null)), [tid, nowKey]);

  // The reason behind a failing integration: the failed step of its newest failed run, one small call per integration
  // (the runs carry no info; the history is only loaded for the changes lens)
  const failing = useMemo(() => {
    const by = new Map<string, { newest: Run; failed: number }>();
    for (const r of runs.data?.list ?? []) {
      if (!r.process || r.failed === 0) continue;
      const cur = by.get(r.process);
      by.set(r.process, { newest: cur && cur.newest.first > r.first ? cur.newest : r, failed: (cur?.failed ?? 0) + 1 });
    }
    return [...by.entries()].sort((a, b) => b[1].failed - a[1].failed || a[0].localeCompare(b[0])).slice(0, 3).map(([process, x]) => ({ process, executionId: x.newest.executionId }));
  }, [runs.data]);
  const failingKey = failing.map((f) => f.executionId).join('|');
  const reasons = useAsync(async () => {
    const out: Record<string, string> = {};
    await Promise.all(failing.map(async ({ process, executionId }) => {
      try {
        const e = await Api.execution(tid, executionId);
        const info = [...e.steps].reverse().find((st) => st.status === 'FAILED' && st.info)?.info
          ?? e.steps.flatMap((st) => st.items).find((h) => h.meta.status === 'FAILED' && h.meta.info)?.meta.info;
        if (info) out[process] = info;
      } catch {
        // no reason to tell, then
      }
    }));
    return out;
  }, [tid, failingKey]);

  const kinds = base.data?.kinds ?? [];
  const type = preview.data?.type ?? overview.data?.type ?? null;
  const own = ownKinds(kinds, preview.data, overview.data);
  // The thing itself is the own kind with one record (a meter's readings, not the consumptions it feeds); a plain
  // value is one thing only when such a kind exists
  const one = own.find((k) => k.records === 1) ?? null;
  const singleKind = type && SINGLE_TYPES.has(type) ? (one ?? own[0] ?? null) : type === 'Value' ? one : null;
  // The list wanted beyond the default: the kind asked for (when it is among what was found), else a single record's own kind
  const knownKind = kind && (kinds.length === 0 || kinds.some((k) => k.cls === kind)) ? kind : null;
  const wanted = knownKind ?? singleKind?.cls ?? null;

  // Lists already loaded for this scope, by data model, so switching kinds and the sentence never wait twice
  const cache = useRef<{ key: string; lists: Map<string, RecordList> }>({ key: scopeKey, lists: new Map() });
  if (cache.current.key !== scopeKey) cache.current = { key: scopeKey, lists: new Map() };
  const remember = (l: RecordList | null | undefined) => {
    if (l) cache.current.lists.set(l.cls, l);
  };
  remember(base.data?.list);
  const baseCls = base.data?.list?.cls ?? null;
  const needExtra = !!wanted && base.data !== undefined && wanted !== baseCls && !cache.current.lists.has(wanted);
  const extra = useAsync(async () => {
    if (!live || !needExtra || !wanted) return null;
    const r = await Api.records(tid, withParams({ cls: wanted, limit: String(limit) }));
    cache.current.lists.set(wanted, r.list ?? ({ ...(r.kinds.find((k) => k.cls === wanted) ?? { cls: wanted, system: '', key: [], label: wanted, records: 0, messages: 0 }), cadenceMs: null, columns: [], steps: [], rows: [], truncated: false } as RecordList));
    return r;
  }, [tid, scopeKey, wanted, needExtra, limit]);
  remember(extra.data?.list);
  const lists = cache.current.lists;

  const list = (wanted ? lists.get(wanted) : null) ?? (!knownKind ? base.data?.list ?? null : null);
  const ownList = singleKind ? lists.get(singleKind.cls) ?? null : null;
  const q0 = q.trim().replace(/^"|"$/g, '').toLowerCase();
  const row = ownList?.rows.find((r) => r.key.toLowerCase() === q0) ?? ownList?.rows.find((r) => Object.values(r.values).some((x) => x.toLowerCase() === q0)) ?? null;
  const single = singleKind && row ? { cls: singleKind.cls, key: row.key } : null;

  const problems = useMemo(() => {
    const out = new Set<string>();
    for (const r of [preview, base, runs, overview, history, extra]) if (r.error) out.add(r.error);
    const nodes = new Map<string, VaultError>();
    for (const d of [preview.data, base.data, runs.data, overview.data, history.data, extra.data]) {
      for (const e of d?.vaultErrors ?? []) nodes.set(e.vaultId, e);
    }
    for (const e of nodes.values()) out.add(`${e.vaultName}: ${e.error}`);
    return [...out];
  }, [preview, base, runs, overview, history, extra]);

  const known = useMemo(() => kindsOf(overview.data, base.data, preview.data), [overview.data, base.data, preview.data]);
  // The data models of the same window seen up to now, remembered for when the handle is dragged back from it
  const seen = useRef<{ key: string; kinds: Kind[] }>({ key: '', kinds: [] });
  if (!nowScope && known.length) seen.current = { key: scopeKey, kinds: known };
  // What the window holds up to now but had not come yet as of the moment: a lane never vanishes, its node greys.
  // The preview knows the documents that carry the searched value; what was linked to them is what was seen before
  const ghostKinds = useMemo(() => {
    if (!nowScope) return [];
    const have = new Set(known.map((k) => k.cls));
    const out: Kind[] = [];
    for (const k of seen.current.key === nowKey ? seen.current.kinds : []) if (!have.has(k.cls)) out.push({ ...k, total: 0, status: [], first: null, last: null });
    for (const c of full.data?.classes ?? []) {
      if (have.has(c.cls) || out.some((k) => k.cls === c.cls)) continue;
      out.push({ cls: c.cls, system: c.system, total: 0, sampled: 0, direct: true, via: [], status: [], format: null, histogram: [], first: null, last: null, fields: [] });
    }
    return out;
  }, [nowScope, nowKey, full.data, known]);
  const mapKinds = useMemo(() => (ghostKinds.length ? [...known, ...ghostKinds] : known), [known, ghostKinds]);
  const ghosts = useMemo(() => ghostKinds.map((k) => k.cls), [ghostKinds]);

  return {
    scopeKey,
    preview: preview.data,
    full: nowScope ? full.data : null,
    ghosts,
    records: base.data,
    kinds,
    mapKinds,
    list,
    ownList,
    single,
    runs: runs.data,
    reasons: reasons.data ?? {},
    overview: overview.data,
    history: history.data,
    loading: preview.loading || base.loading || runs.loading || overview.loading,
    listLoading: base.loading || (needExtra && extra.loading),
    problems,
    reload: () => {
      preview.reload();
      base.reload();
      runs.reload();
      overview.reload();
    },
  };
}
